import { useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import TextInput from "../../components/forms/TextInput";
import { getAccountSettings, saveAccountSettings } from "../../js/api/accountSettings";
import { getAgentProfile, saveAgentProfile } from "../../js/api/agentProfile";
import { getBrokerProfile, saveBrokerProfile } from "../../js/api/brokerProfile";
import { useAuth } from "../../js/hooks/useAuth";
import { hasAgentAccess, hasBrokerAccess } from "../../js/auth/authService";
import { productLabel } from "../../js/auth/packageAccess";
import { listTable } from "../../js/db";
import { setCredential } from "../../js/db/credentials";
import { REPTC_ABOUT } from "../../js/utils/reptc";
import AccountLifecycle from "./AccountLifecycle";
import { noticesForSession } from "../../js/api/accountLifecycle";
import SandboxBillingPanel from "../../components/billing/SandboxBillingPanel";
import RestoreTimeline from "../../components/restore/RestoreTimeline";
import BackupExportPanel from "../../components/backup/BackupExportPanel";

const TABS = [
  { id: "profile", label: "Profile & Identity" },
  { id: "privacy", label: "Privacy & Security" },
  { id: "lifecycle", label: "Account Lifecycle" },
  { id: "backup", label: "Backup & Export" },
  { id: "restore", label: "Restore Points" },
  { id: "office", label: "Office / Brokerage" },
  { id: "portal", label: "Client Portal" },
  { id: "notifications", label: "Notifications" },
  { id: "billing", label: "Subscription" },
];

export default function AccountPortal() {
  const { session, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const isBroker = hasBrokerAccess(session?.role);
  const isAgent = hasAgentAccess(session?.role);
  const tab = params.get("tab") || "profile";
  const tabs = TABS.filter((item) => item.id !== "office" || isBroker);
  const [flash, setFlash] = useState("");

  if (!isAgent && !isBroker && session?.role !== "admin" && session?.role !== "corporate") {
    return <Navigate to="/agent/login" replace />;
  }

  function go(id) {
    setParams({ tab: id });
    setFlash("");
  }

  return (
    <AppLayout
      variant={
        session?.role === "admin" ? "admin" : isBroker && session?.role !== "agent" ? "broker" : "agent"
      }
    >
      <div className="account-portal">
        <header className="page-header">
          <div>
            <h1>Account</h1>
            <p className="page-lead">
              Identity, privacy, and how clients see you. This desk is for professionals only.
            </p>
            {session?.role && session.role !== "admin" ? (
              <p className="badge role-tag" style={{ marginTop: "0.65rem" }}>
                {productLabel(session)}
              </p>
            ) : null}
            <details className="account-about">
              <summary>About this system</summary>
              <p>{REPTC_ABOUT}</p>
            </details>
          </div>
        </header>
        {flash ? <p className="confirm-banner">{flash}</p> : null}
        <nav className="account-tabs" aria-label="Account sections">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`account-tabs__btn${tab === item.id ? " is-active" : ""}`}
              onClick={() => go(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        {tab === "profile" ? (
          <ProfileTab session={session} isBroker={isBroker && session?.role === "broker"} onFlash={setFlash} />
        ) : null}
        {tab === "privacy" ? <PrivacyTab session={session} logout={logout} onFlash={setFlash} /> : null}
        {tab === "lifecycle" ? <AccountLifecycle /> : null}
        {tab === "backup" ? <BackupExportPanel session={session} onFlash={setFlash} compact /> : null}
        {tab === "restore" ? (
          <RestoreTimeline
            session={session}
            userId={session.userId}
            title="Your restore timeline"
            lead="Edits to your desk, settings, and messages are saved automatically on this device."
            onFlash={setFlash}
          />
        ) : null}
        {tab === "office" && isBroker ? <OfficeTab session={session} onFlash={setFlash} /> : null}
        {tab === "portal" ? <PortalTab session={session} onFlash={setFlash} /> : null}
        {tab === "notifications" ? <NotifyTab session={session} onFlash={setFlash} /> : null}
        {tab === "billing" ? <SandboxBillingPanel session={session} onFlash={setFlash} /> : null}
      </div>
    </AppLayout>
  );
}

function ProfileTab({ session, isBroker, onFlash }) {
  const [form, setForm] = useState(() =>
    isBroker ? getBrokerProfile(session.userId, session) : getAgentProfile(session.userId, session)
  );

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function onPhoto(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((current) => ({ ...current, photo: String(reader.result || "") }));
    reader.readAsDataURL(file);
  }

  function save(event) {
    event.preventDefault();
    if (isBroker) saveBrokerProfile(session.userId, form);
    else saveAgentProfile(session.userId, form);
    onFlash("Profile saved on this device. It now feeds your desk header and client portals.");
  }

  return (
    <form className="card account-card" onSubmit={save}>
      <h2>Profile & Identity</h2>
      <p className="muted">These fields appear on your desk and, when you allow it, on the client portal.</p>
      {isBroker ? (
        <>
          <TextInput label="Full name" name="broker_name" value={form.broker_name} onChange={update} />
          <TextInput label="Title" name="broker_title" value={form.broker_title} onChange={update} />
          <TextInput label="License number" name="license" value={form.license} onChange={update} />
        </>
      ) : (
        <>
          <TextInput label="Full name" name="agent_name" value={form.agent_name} onChange={update} />
          <TextInput label="Title" name="agent_title" value={form.agent_title} onChange={update} />
          <TextInput label="License number" name="dre_license" value={form.dre_license} onChange={update} />
        </>
      )}
      <TextInput label="Office name" name="office_name" value={form.office_name} onChange={update} />
      <TextInput label="Office address" name="office_address" value={form.office_address} onChange={update} />
      <label className="field-label" htmlFor="display-format">
        How your name is written
      </label>
      <select
        id="display-format"
        className="field-select"
        name="display_format"
        value={form.display_format || "name-title"}
        onChange={update}
      >
        <option value="name-title">Name, title</option>
        <option value="title-name">Title name</option>
        <option value="name-only">Name only</option>
      </select>
      <div className="two-col">
        <TextInput
          label="Phone"
          name={isBroker ? "broker_phone" : "agent_phone"}
          value={isBroker ? form.broker_phone : form.agent_phone}
          onChange={update}
        />
        <TextInput
          label="Email"
          name={isBroker ? "broker_email" : "agent_email"}
          type="email"
          value={isBroker ? form.broker_email : form.agent_email}
          onChange={update}
        />
      </div>
      <label className="field-label" htmlFor="account-photo">
        Profile photo
      </label>
      <input id="account-photo" type="file" accept="image/*" onChange={onPhoto} />
      {form.photo ? <img src={form.photo} alt="" className="account-photo" /> : null}
      <p className="field-hint">License numbers are yours to record. This is not a California DRE certification.</p>
      <button type="submit" className="primary-btn">
        Save identity
      </button>
    </form>
  );
}

function PrivacyTab({ session, logout, onFlash }) {
  const [form, setForm] = useState(() => getAccountSettings(session.userId));
  const [password, setPassword] = useState("");

  function save(event) {
    event.preventDefault();
    if (password.trim()) setCredential(session.userId, password.trim());
    saveAccountSettings(session.userId, form);
    setPassword("");
    onFlash("Privacy preferences saved on this device.");
  }

  return (
    <form className="card account-card" onSubmit={save}>
      <h2>Privacy & Security</h2>
      <p className="muted">
        Your account and client data are managed with strict privacy and security controls. See also the{" "}
        <Link to="/eula">EULA</Link> and <Link to="/disclaimer">Disclaimer</Link>.
      </p>
      <TextInput
        label="New password (local prototype)"
        name="password"
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />
      <label className="choice-chip">
        <input
          type="checkbox"
          checked={form.privacy.twoFactor}
          onChange={(event) =>
            setForm((current) => ({
              ...current,
              privacy: { ...current.privacy, twoFactor: event.target.checked },
            }))
          }
        />
        Enable two-factor reminder (hosted 2FA is not connected yet)
      </label>
      <TextInput
        label="Recovery email"
        name="recoveryEmail"
        type="email"
        value={form.privacy.recoveryEmail}
        onChange={(event) =>
          setForm((current) => ({
            ...current,
            privacy: { ...current.privacy, recoveryEmail: event.target.value },
          }))
        }
      />
      <h3>Devices and sessions</h3>
      <ul className="account-sessions">
        {(form.sessions || []).length ? (
          form.sessions.map((row) => (
            <li key={row.at}>
              {new Date(row.at).toLocaleString()} · {row.label}
            </li>
          ))
        ) : (
          <li className="muted">No other devices recorded on this browser.</li>
        )}
      </ul>
      <div className="btn-row">
        <button type="submit" className="primary-btn">
          Save privacy
        </button>
        <button
          type="button"
          className="ghost-btn"
          onClick={() => {
            logout();
            onFlash("Signed out of this browser.");
          }}
        >
          Sign out this session
        </button>
        <button
          type="button"
          className="ghost-btn"
          onClick={() => {
            saveAccountSettings(session.userId, { ...form, sessions: [] });
            logout();
          }}
        >
          Sign out all sessions
        </button>
      </div>
      <p className="muted">
        To close this desk, use{" "}
        <Link to="/account?tab=lifecycle&card=cancel">Cancel Account</Link> in Account Lifecycle.
        Transaction records stay with the office.
      </p>
    </form>
  );
}

function OfficeTab({ session, onFlash }) {
  const [form, setForm] = useState(() => getBrokerProfile(session.userId, session));
  const [inviteEmail, setInviteEmail] = useState("");
  const roster = useMemo(
    () => listTable("users").filter((user) => user.role_type === "agent" && user.brokerage_id === session.userId),
    [session.userId]
  );

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function onLogo(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((current) => ({ ...current, office_logo: String(reader.result || "") }));
    reader.readAsDataURL(file);
  }

  function save(event) {
    event.preventDefault();
    saveBrokerProfile(session.userId, form);
    onFlash("Office settings saved. Agent desks and client portals read this office name.");
  }

  function invite(event) {
    event.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    const pending = [...(form.pending_invites || []), { email, at: new Date().toISOString() }];
    const next = { ...form, pending_invites: pending };
    setForm(next);
    saveBrokerProfile(session.userId, next);
    setInviteEmail("");
    onFlash(`Invite recorded for ${email}. Hosted email is not connected on this prototype.`);
  }

  return (
    <form className="card account-card" onSubmit={save}>
      <h2>Office / Brokerage</h2>
      <TextInput label="Office name" name="office_name" value={form.office_name} onChange={update} />
      <TextInput label="Office phone" name="office_phone" value={form.office_phone} onChange={update} />
      <TextInput label="Office email" name="office_email" type="email" value={form.office_email} onChange={update} />
      <TextInput label="Office address" name="office_address" value={form.office_address} onChange={update} />
      <label className="field">
        <span className="field-label">Compliance notices</span>
        <textarea
          className="field-input"
          name="compliance_notices"
          rows={3}
          value={form.compliance_notices || ""}
          onChange={update}
        />
        <span className="field-hint">Office notes for your desk. This is not a California DRE certification.</span>
      </label>
      <label className="field-label" htmlFor="office-logo">
        Office logo
      </label>
      <input id="office-logo" type="file" accept="image/*" onChange={onLogo} />
      {form.office_logo ? <img src={form.office_logo} alt="" className="account-photo" /> : null}
      <label className="choice-chip">
        <input
          type="checkbox"
          checked={Boolean(form.agents_may_edit_office)}
          onChange={(event) =>
            setForm((current) => ({ ...current, agents_may_edit_office: event.target.checked }))
          }
        />
        Agents may help manage office settings
      </label>
      <p>
        <Link className="primary-btn" to="/broker/controls/agents">
          Agent lifecycle controls
        </Link>
      </p>
      <h3>Agent roster</h3>
      <ul className="account-sessions">
        {roster.length ? (
          roster.map((agent) => (
            <li key={agent.id}>
              {agent.name} · {agent.email}
            </li>
          ))
        ) : (
          <li className="muted">No agents are linked to this office yet.</li>
        )}
      </ul>
      <div className="two-col">
        <TextInput
          label="Invite an agent (email)"
          name="invite"
          type="email"
          value={inviteEmail}
          onChange={(event) => setInviteEmail(event.target.value)}
        />
        <button type="button" className="ghost-btn" onClick={invite}>
          Record invite
        </button>
      </div>
      {(form.pending_invites || []).length ? (
        <ul className="account-sessions">
          {form.pending_invites.map((row) => (
            <li key={`${row.email}-${row.at}`}>Pending · {row.email}</li>
          ))}
        </ul>
      ) : null}
      <p className="field-hint">Invites will email from the hosted desk. This prototype keeps the roster local.</p>
      <button type="submit" className="primary-btn">
        Save office
      </button>
    </form>
  );
}

function PortalTab({ session, onFlash }) {
  const [form, setForm] = useState(() => getAccountSettings(session.userId));

  function toggle(key) {
    setForm((current) => ({
      ...current,
      portal: { ...current.portal, [key]: !current.portal[key] },
    }));
  }

  function save(event) {
    event.preventDefault();
    saveAccountSettings(session.userId, form);
    onFlash("Client portal settings saved.");
  }

  return (
    <form className="card account-card" onSubmit={save}>
      <h2>Client Portal Settings</h2>
      <TextInput
        label="How your name appears"
        name="advisorLabel"
        value={form.portal.advisorLabel}
        onChange={(event) =>
          setForm((current) => ({
            ...current,
            portal: { ...current.portal, advisorLabel: event.target.value },
          }))
        }
      />
      <p className="field-hint">Example: Your Advisor</p>
      {[
        ["showTitle", "Show title with name"],
        ["showPhone", "Show phone"],
        ["showEmail", "Show email"],
        ["showOffice", "Show office"],
        ["allowMessages", "Clients may send messages"],
        ["allowUploads", "Clients may upload documents"],
        ["showProgress", "Show progress"],
        ["showTips", "Show tips and guidance"],
      ].map(([key, label]) => (
        <label key={key} className="choice-chip">
          <input type="checkbox" checked={Boolean(form.portal[key])} onChange={() => toggle(key)} />
          {label}
        </label>
      ))}
      <label className="field-label" htmlFor="default-portal">
        Default portal type
      </label>
      <select
        id="default-portal"
        className="field-select"
        value={form.portal.defaultPortal}
        onChange={(event) =>
          setForm((current) => ({
            ...current,
            portal: { ...current.portal, defaultPortal: event.target.value },
          }))
        }
      >
        <option value="buyer">Buyer</option>
        <option value="seller">Seller</option>
        <option value="both">Both (by client type)</option>
      </select>
      <button type="submit" className="primary-btn">
        Save portal settings
      </button>
    </form>
  );
}

function NotifyTab({ session, onFlash }) {
  const [form, setForm] = useState(() => getAccountSettings(session.userId));
  const notices = noticesForSession(session);

  function save(event) {
    event.preventDefault();
    saveAccountSettings(session.userId, form);
    onFlash("Notification preferences saved.");
  }

  return (
    <form className="card account-card" onSubmit={save}>
      <h2>Notifications</h2>
      {notices.length ? (
        <ul className="account-sessions">
          {notices.slice(0, 6).map((row) => (
            <li key={row.id}>
              {new Date(row.at).toLocaleString()} · {row.agentText}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No account lifecycle notices on this device.</p>
      )}
      {[
        ["email", "Email notifications"],
        ["sms", "SMS notifications (when connected)"],
        ["calendar", "Calendar reminders"],
        ["tasks", "Task updates"],
        ["activity", "Activity alerts"],
      ].map(([key, label]) => (
        <label key={key} className="choice-chip">
          <input
            type="checkbox"
            checked={Boolean(form.notifications[key])}
            onChange={() =>
              setForm((current) => ({
                ...current,
                notifications: { ...current.notifications, [key]: !current.notifications[key] },
              }))
            }
          />
          {label}
        </label>
      ))}
      <label className="field-label" htmlFor="notify-freq">
        Frequency
      </label>
      <select
        id="notify-freq"
        className="field-select"
        value={form.notifications.frequency}
        onChange={(event) =>
          setForm((current) => ({
            ...current,
            notifications: { ...current.notifications, frequency: event.target.value },
          }))
        }
      >
        <option value="immediate">Immediate</option>
        <option value="daily">Daily digest</option>
        <option value="weekly">Weekly digest</option>
      </select>
      <button type="submit" className="primary-btn">
        Save notifications
      </button>
    </form>
  );
}

